import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LocalDate } from "@/components/ui/local-date";
import { ApiError, api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useState } from "react";

/**
 * `/accounting/recurring` — the invoices that bill themselves.
 *
 * Hosting and maintenance go out every month whether anyone remembers
 * or not, which is why this page is mostly about what is *due* rather
 * than what exists: a template that stopped billing is only noticed
 * when the money doesn't arrive.
 */

type Line = {
  id: string;
  position: number;
  description: string;
  quantity: string;
  unitName: string | null;
  unitPrice: string;
  vatRate: string;
};

type Template = {
  id: string;
  name: string;
  active: boolean;
  monthsPeriod: number;
  startsOn: string | null;
  endsOn: string | null;
  nextOccurrenceOn: string | null;
  dueDays: number | null;
  currency: string;
  note: string | null;
  due: boolean;
  lines: Line[];
  customer: { id: string; name: string } | null;
  contact: { id: string; name: string } | null;
  lastInvoice: { id: string; number: string; issuedOn: string; total: string } | null;
};

export const Route = createFileRoute("/_app/accounting/recurring")({
  loader: async () => {
    try {
      return await api.get<{ templates: Template[] }>("/api/accounting/recurring");
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: RecurringPage,
});

function money(amount: string, currency: string): string {
  return `${Number(amount).toLocaleString("cs-CZ", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency}`;
}

function periodLabel(months: number): string {
  if (months === 1) return "monthly";
  if (months === 3) return "quarterly";
  if (months === 6) return "twice a year";
  if (months === 12) return "yearly";
  return `every ${months} months`;
}

function templateTotal(template: Template): string {
  const total = template.lines.reduce((sum, line) => {
    const net = Number(line.quantity) * Number(line.unitPrice);
    return sum + net * (1 + Number(line.vatRate) / 100);
  }, 0);
  return money(total.toFixed(2), template.currency);
}

function RecurringPage() {
  const { templates } = Route.useLoaderData();
  const router = useRouter();
  const [adding, setAdding] = useState(false);

  const run = useApiMutation(() => api.post("/api/accounting/recurring/run", {}), {
    onSuccess: () => router.invalidate(),
    errorMessage: () => "Couldn't queue the run.",
  });

  const due = templates.filter((t) => t.due);

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-semibold text-2xl">Recurring</h1>
          <p className="text-muted-foreground text-sm">
            {due.length > 0
              ? `${due.length} template(s) due — the daily job issues them, the button only makes it happen now.`
              : "Nothing due. The daily job issues each template on its own date."}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" disabled={run.pending} onClick={() => run.mutate()}>
            {run.pending ? "Queueing…" : "Issue what's due"}
          </Button>
          <Button onClick={() => setAdding((value) => !value)}>
            {adding ? "Close" : "New template"}
          </Button>
        </div>
      </div>

      {run.error && <p className="text-destructive text-sm">{run.error}</p>}

      {adding && (
        <TemplateForm
          onDone={() => {
            setAdding(false);
            router.invalidate();
          }}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Templates</CardTitle>
          <CardDescription>
            A paused template stops billing but keeps its history — the invoices it already issued
            stay where they are.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {templates.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No templates yet. The Fakturoid import brings the existing generators across.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1 pr-4 font-medium">Name</th>
                  <th className="py-1 pr-4 font-medium">Partner</th>
                  <th className="py-1 pr-4 font-medium">Period</th>
                  <th className="py-1 pr-4 font-medium">Next</th>
                  <th className="py-1 pr-4 text-right font-medium">Amount</th>
                  <th className="py-1 pr-4 font-medium">Last invoice</th>
                  <th className="py-1 font-medium" />
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <TemplateRow key={template.id} template={template} />
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function TemplateRow({ template }: { template: Template }) {
  const router = useRouter();
  const [editing, setEditing] = useState(false);

  const issue = useApiMutation(
    () => api.post(`/api/accounting/recurring/${template.id}/issue`, {}),
    { onSuccess: () => router.invalidate(), errorMessage: () => "Couldn't issue it." },
  );

  const toggle = useApiMutation(
    () => api.patch(`/api/accounting/recurring/${template.id}`, { active: !template.active }),
    { onSuccess: () => router.invalidate(), errorMessage: () => "Couldn't change it." },
  );

  return (
    <>
      <tr className="border-t align-top">
        <td className="py-1 pr-4">
          {template.name}
          {!template.active && (
            <Badge variant="secondary" className="ml-2">
              paused
            </Badge>
          )}
          {template.due && template.active && (
            <Badge variant="warning" className="ml-2">
              due
            </Badge>
          )}
          {(issue.error || toggle.error) && (
            <span className="block text-destructive text-xs">{issue.error ?? toggle.error}</span>
          )}
        </td>
        <td className="py-1 pr-4 text-muted-foreground">
          {template.contact?.name ?? template.customer?.name ?? "—"}
        </td>
        <td className="py-1 pr-4 text-muted-foreground">{periodLabel(template.monthsPeriod)}</td>
        <td className="py-1 pr-4">
          {template.nextOccurrenceOn ? <LocalDate value={template.nextOccurrenceOn} /> : "—"}
        </td>
        <td className="py-1 pr-4 text-right tabular-nums">{templateTotal(template)}</td>
        <td className="py-1 pr-4 text-muted-foreground">
          {template.lastInvoice ? (
            <>
              {template.lastInvoice.number} · <LocalDate value={template.lastInvoice.issuedOn} />
            </>
          ) : (
            "never"
          )}
        </td>
        <td className="py-1">
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="text-sm underline"
              disabled={issue.pending || !template.active}
              onClick={() => issue.mutate()}
            >
              {issue.pending ? "Issuing…" : "Issue now"}
            </button>
            <button
              type="button"
              className="text-muted-foreground text-sm underline"
              disabled={toggle.pending}
              onClick={() => toggle.mutate()}
            >
              {template.active ? "pause" : "resume"}
            </button>
            <button
              type="button"
              className="text-muted-foreground text-sm underline"
              onClick={() => setEditing((value) => !value)}
            >
              {editing ? "close" : "edit"}
            </button>
          </div>
        </td>
      </tr>
      {editing && (
        <tr>
          <td colSpan={7} className="py-2">
            <TemplateForm
              template={template}
              onDone={() => {
                setEditing(false);
                router.invalidate();
              }}
            />
          </td>
        </tr>
      )}
    </>
  );
}

const EMPTY_LINE = { description: "", quantity: "1", unitPrice: "", vatRate: "21" };

function TemplateForm({ template, onDone }: { template?: Template; onDone: () => void }) {
  const [contacts, setContacts] = useState<Array<{ id: string; name: string }>>([]);
  const [lines, setLines] = useState(
    template
      ? template.lines.map((line) => ({
          description: line.description,
          quantity: String(Number(line.quantity)),
          unitPrice: String(Number(line.unitPrice)),
          vatRate: String(Number(line.vatRate)),
        }))
      : [EMPTY_LINE],
  );
  const [contactValue, setContactValue] = useState(template?.contact?.id ?? "");

  const save = useApiMutation(
    (body: Record<string, unknown>) =>
      template
        ? api.patch(`/api/accounting/recurring/${template.id}`, body)
        : api.post("/api/accounting/recurring", body),
    {
      onSuccess: onDone,
      errorMessage: () =>
        template ? "Couldn't save the template." : "Couldn't create the template.",
    },
  );

  if (contacts.length === 0) {
    void api
      .get<{ contacts: Array<{ id: string; name: string }> }>(
        "/api/accounting/contacts?role=customer",
      )
      .then((result) => setContacts(result.contacts))
      .catch(() => undefined);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{template ? "Edit template" : "New template"}</CardTitle>
        <CardDescription>
          {template
            ? "Changes apply from the next invoice; the ones already issued stay as they are."
            : "The first invoice goes out on the start date — the schedule starts with it, not after it."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            const form = new FormData(e.currentTarget);
            const date = String(form.get("startsOn") ?? "");
            const dueDays = String(form.get("dueDays") ?? "").trim();
            const contactId = String(form.get("contactId") ?? "") || null;
            save.mutate({
              name: String(form.get("name") ?? ""),
              ...(template && !contactId ? {} : { contactId }),
              monthsPeriod: Number(form.get("monthsPeriod") ?? 1),
              ...(template ? { nextOccurrenceOn: date } : { startsOn: date }),
              dueDays: dueDays.length > 0 ? Number(dueDays) : null,
              currency: String(form.get("currency") ?? "CZK"),
              lines: lines
                .filter((line) => line.description.trim().length > 0)
                .map((line) => ({
                  description: line.description,
                  quantity: Number(line.quantity),
                  unitPrice: Number(line.unitPrice),
                  vatRate: Number(line.vatRate),
                })),
            });
          }}
        >
          <div className="grid gap-3 md:grid-cols-4">
            <label className="text-sm md:col-span-2">
              <span className="text-muted-foreground text-xs">Name</span>
              <input
                name="name"
                required
                defaultValue={template?.name}
                placeholder="Hosting — monthly"
                className="mt-1 block h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              />
            </label>
            <label className="text-sm">
              <span className="text-muted-foreground text-xs">Contact</span>
              <select
                name="contactId"
                value={contactValue}
                onChange={(e) => setContactValue(e.target.value)}
                className="mt-1 block h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              >
                <option value="">—</option>
                {contacts.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {contact.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="text-muted-foreground text-xs">Every (months)</span>
              <input
                name="monthsPeriod"
                type="number"
                min={1}
                max={12}
                defaultValue={template?.monthsPeriod ?? 1}
                className="mt-1 block h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              />
            </label>
            <label className="text-sm">
              <span className="text-muted-foreground text-xs">
                {template ? "Next invoice on" : "First invoice on"}
              </span>
              <input
                name="startsOn"
                type="date"
                required
                defaultValue={template?.nextOccurrenceOn?.slice(0, 10)}
                className="mt-1 block h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              />
            </label>
            <label className="text-sm">
              <span className="text-muted-foreground text-xs">Currency</span>
              <input
                name="currency"
                defaultValue={template?.currency ?? "CZK"}
                className="mt-1 block h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              />
            </label>
            <label className="text-sm">
              <span className="text-muted-foreground text-xs">Due in (days)</span>
              <input
                name="dueDays"
                type="number"
                min={0}
                max={365}
                defaultValue={template?.dueDays ?? ""}
                placeholder="From the client"
                className="mt-1 block h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              />
            </label>
          </div>

          <div className="space-y-2">
            {lines.map((line, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional
              <div key={index} className="grid gap-2 md:grid-cols-6">
                <input
                  value={line.description}
                  placeholder="Description"
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm md:col-span-2"
                  onChange={(e) =>
                    setLines((rows) =>
                      rows.map((row, i) =>
                        i === index ? { ...row, description: e.target.value } : row,
                      ),
                    )
                  }
                />
                <input
                  value={line.quantity}
                  type="number"
                  step="0.01"
                  placeholder="Qty"
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  onChange={(e) =>
                    setLines((rows) =>
                      rows.map((row, i) =>
                        i === index ? { ...row, quantity: e.target.value } : row,
                      ),
                    )
                  }
                />
                <input
                  value={line.unitPrice}
                  type="number"
                  step="0.01"
                  placeholder="Unit price"
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  onChange={(e) =>
                    setLines((rows) =>
                      rows.map((row, i) =>
                        i === index ? { ...row, unitPrice: e.target.value } : row,
                      ),
                    )
                  }
                />
                <input
                  value={line.vatRate}
                  type="number"
                  step="0.01"
                  placeholder="VAT %"
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  onChange={(e) =>
                    setLines((rows) =>
                      rows.map((row, i) =>
                        i === index ? { ...row, vatRate: e.target.value } : row,
                      ),
                    )
                  }
                />
                <button
                  type="button"
                  className="text-left text-muted-foreground text-sm underline disabled:opacity-50"
                  disabled={lines.length === 1}
                  onClick={() => setLines((rows) => rows.filter((_, i) => i !== index))}
                >
                  remove
                </button>
              </div>
            ))}
            <button
              type="button"
              className="text-muted-foreground text-sm underline"
              onClick={() => setLines((rows) => [...rows, EMPTY_LINE])}
            >
              Add a line
            </button>
          </div>

          <div className="flex items-center gap-3">
            <Button type="submit" disabled={save.pending}>
              {save.pending ? "Saving…" : template ? "Save" : "Create"}
            </Button>
            {save.error && <p className="text-destructive text-sm">{save.error}</p>}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
